import {
  HHAlert,
  HHBox,
  HHCard,
  HHCardContent,
  HHCircularProgress,
  HHTypography,
} from '@hinge-health/react-component-library';
import { AlertColor } from '@mui/material';
import { VALIDATION_STATUS } from '../constants/partner-data-gateway.constant';
import { ValidationReport } from '../domain/types';
import { useIngestionValidation } from '../hooks/use-ingestion-validation';
import { formatWireLabel } from '../utils/format.utils';
import { printDateAndTime } from '../utils/print-date-and-time';
import { JsonViewer } from './json-viewer';

export const VALIDATION_TAB_TEST_ID = 'ingestion-validation-tab';
export const VALIDATION_LOADING_TEST_ID = 'validation-loading';
export const VALIDATION_ERROR_TEST_ID = 'validation-error-alert';
export const VALIDATION_EMPTY_STATE_TEST_ID = 'validation-empty-state';
export const VALIDATION_STATUS_BANNER_TEST_ID = 'validation-status-banner';
export const VALIDATION_REPORT_CARD_TEST_ID = 'validation-report-card';

export const VALIDATION_EMPTY_STATE_MESSAGE =
  'Validation has not run yet for this ingestion.';
export const VALIDATION_ERROR_MESSAGE =
  'Could not load the validation report. Please try again.';

/**
 * Banner colour per verdict. An unrecognised verdict falls back to a neutral
 * `info` banner rather than being hidden — the status is still shown, it
 * just is not colour-coded as pass or fail.
 */
const STATUS_SEVERITY: Record<string, AlertColor | undefined> = {
  [VALIDATION_STATUS.Passed]: 'success',
  [VALIDATION_STATUS.Failed]: 'error',
  [VALIDATION_STATUS.Warning]: 'warning',
};

const UNKNOWN_STATUS_SEVERITY: AlertColor = 'info';

export const severityForValidationStatus = (status: string): AlertColor =>
  STATUS_SEVERITY[status] ?? UNKNOWN_STATUS_SEVERITY;

const ValidationStatusBanner = ({
  validation,
}: {
  validation: ValidationReport;
}): JSX.Element => {
  const severity = severityForValidationStatus(validation.status);

  return (
    // testid and severity sit on the wrapper: HHAlert does not forward
    // unknown props to the DOM, so tests cannot read them off the alert.
    <HHBox
      data-testid={VALIDATION_STATUS_BANNER_TEST_ID}
      data-severity={severity}
      marginBottom={2}
    >
      <HHAlert hhVariant="outlined" severity={severity}>
        <HHTypography hhVariant="body1">
          {`Validation ${formatWireLabel(validation.status)}`}
        </HHTypography>
        <HHTypography hhVariant="body2" color="textSecondary">
          {printDateAndTime(validation.createdAt)}
        </HHTypography>
      </HHAlert>
    </HHBox>
  );
};

export interface ValidationTabProps {
  eventId: string;
}

/**
 * The `?tab=validation` panel: a verdict banner plus the raw validation
 * report.
 *
 * The report itself is an opaque JSONB payload owned by the validator, so it
 * is rendered verbatim through `<JsonViewer />` rather than being given a
 * bespoke layout this module would have to keep in step with PDGS.
 */
export const ValidationTab = ({ eventId }: ValidationTabProps): JSX.Element => {
  const { validation, isLoading, hasError } = useIngestionValidation(eventId);

  return (
    <HHBox data-testid={VALIDATION_TAB_TEST_ID}>
      {isLoading && (
        <HHBox
          data-testid={VALIDATION_LOADING_TEST_ID}
          display="flex"
          justifyContent="center"
          paddingY={4}
        >
          <HHCircularProgress />
        </HHBox>
      )}

      {!isLoading && hasError && (
        <HHBox data-testid={VALIDATION_ERROR_TEST_ID} paddingY={2}>
          <HHAlert hhVariant="outlined" severity="error">
            {VALIDATION_ERROR_MESSAGE}
          </HHAlert>
        </HHBox>
      )}

      {/* A missing report is a normal state for an in-flight ingestion, so
          it reads as an empty state rather than as a failure. */}
      {!isLoading && !hasError && !validation && (
        <HHBox data-testid={VALIDATION_EMPTY_STATE_TEST_ID} paddingY={4}>
          <HHTypography hhVariant="body2" color="textSecondary">
            {VALIDATION_EMPTY_STATE_MESSAGE}
          </HHTypography>
        </HHBox>
      )}

      {!isLoading && !hasError && validation && (
        <>
          <ValidationStatusBanner validation={validation} />
          <HHCard data-testid={VALIDATION_REPORT_CARD_TEST_ID}>
            <HHCardContent>
              <HHTypography hhVariant="h3">Validation Report</HHTypography>
              <JsonViewer
                value={validation.report}
                label="Validation report"
                defaultExpanded
              />
            </HHCardContent>
          </HHCard>
        </>
      )}
    </HHBox>
  );
};
